<script lang="ts" setup>
import { computed, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import type { Project } from "@/types";
import {
  useProjectConnectionBindings,
  useBindConnection,
  useUnbindConnection,
  type ProjectConnectionSlotEntry,
} from "@/queries/projects";
import { useConnections } from "@/queries/connections";
import { useAlertStore } from "@/stores/alerts-store";

interface Props {
  project: Project | null;
}

const props = defineProps<Props>();
const projectIdRef = computed(() => props.project?.id ?? "");
const enabledRef = computed(() => !!projectIdRef.value);

const route = useRoute();
const router = useRouter();
const alertStore = useAlertStore();

const { bindings, bindingsLoading, bindingsError, refetchBindings } =
  useProjectConnectionBindings(projectIdRef, enabledRef);
const { connections, connectionsLoading } = useConnections();
const { bindConnectionAsync, bindConnectionPending, bindConnectionError } =
  useBindConnection(projectIdRef);
const { unbindConnectionAsync, unbindConnectionPending } = useUnbindConnection(projectIdRef);

// A slot's identity under model D is (name, templateId): two slots can share a name across
// workflows while differing in template, so per-row state is keyed by both.
function slotKey(slot: ProjectConnectionSlotEntry): string {
  return `${slot.connectionTemplateId}::${slot.slotName}`;
}

// Track per-row pending state so multiple slots can be edited without one blocking the other.
const pendingKey = ref<string | null>(null);
// User's pending selection per slot (before pressing Save) — keyed by slot identity.
const draftSelection = ref<Record<string, string>>({});

const slots = computed(() => bindings.value?.slots ?? []);

// Only connections of the slot's template can satisfy it — the bind handler enforces the same match.
function connectionsForSlot(slot: ProjectConnectionSlotEntry) {
  return connections.value.filter((c) => c.templateId === slot.connectionTemplateId);
}

function isPending(slot: ProjectConnectionSlotEntry): boolean {
  return pendingKey.value === slotKey(slot);
}

function currentSelection(slot: ProjectConnectionSlotEntry): string {
  const draft = draftSelection.value[slotKey(slot)];
  if (draft !== undefined) return draft;
  return slot.boundConnectionId ?? "";
}

function setDraft(slot: ProjectConnectionSlotEntry, value: string) {
  draftSelection.value[slotKey(slot)] = value;
}

async function saveBinding(slot: ProjectConnectionSlotEntry) {
  const connectionInstanceId = currentSelection(slot);
  if (!connectionInstanceId) return;
  const key = slotKey(slot);
  pendingKey.value = key;
  try {
    await bindConnectionAsync({
      slotName: slot.slotName,
      connectionTemplateId: slot.connectionTemplateId,
      connectionInstanceId,
    });
    delete draftSelection.value[key];
    await refetchBindings();
  } catch {
    // Surfaced via bindConnectionError
  } finally {
    pendingKey.value = null;
  }
}

async function clearBinding(slot: ProjectConnectionSlotEntry) {
  const key = slotKey(slot);
  pendingKey.value = key;
  try {
    await unbindConnectionAsync({
      slotName: slot.slotName,
      connectionTemplateId: slot.connectionTemplateId,
    });
    delete draftSelection.value[key];
    await refetchBindings();
  } finally {
    pendingKey.value = null;
  }
}

function hasChange(slot: ProjectConnectionSlotEntry): boolean {
  const draft = draftSelection.value[slotKey(slot)];
  if (draft === undefined) return false;
  return draft !== (slot.boundConnectionId ?? "");
}

// The connection-create flow returns here with ?bound=<newId>&slotName=<slot> to auto-bind the
// slot the user came from. Guarded so it runs once; strips the query so a refresh won't replay it.
const autoBindHandled = ref(false);

async function handleAutoBind() {
  if (autoBindHandled.value) return;
  const boundId = route.query.bound as string | undefined;
  const slotName = route.query.slotName as string | undefined;
  if (!boundId || !slotName) return;
  // Wait for slots AND connections — the new connection isn't in the list until its query settles,
  // and its template is what disambiguates the slot when a name repeats across templates.
  if (bindingsLoading.value || connectionsLoading.value || slots.value.length === 0) return;
  const conn = connections.value.find((c) => c.id === boundId);
  if (!conn) return; // connection not visible yet — a later tick (query settled) will retry

  autoBindHandled.value = true;
  // Match on (name, template) — same identity as slotKey — for when a name repeats across templates.
  const target = slots.value.find(
    (s) => s.slotName === slotName && s.connectionTemplateId === conn.templateId,
  );

  const nextQuery = { ...route.query };
  delete nextQuery.bound;
  delete nextQuery.slotName;
  router.replace({ query: nextQuery });

  if (!target) return; // slot no longer present (e.g. workflow changed)

  try {
    await bindConnectionAsync({
      slotName: target.slotName,
      connectionTemplateId: target.connectionTemplateId,
      connectionInstanceId: boundId,
    });
    await refetchBindings();
    alertStore.addSuccess("Connected", `Bound ${conn.name} to ${target.slotName}.`);
  } catch {
    alertStore.addError(
      "Couldn't bind automatically",
      "Your connection was created — select it in the slot below to bind it.",
    );
  }
}

// Re-check as each dependency settles — the query may be set before slots/connections load.
watch(
  [() => route.query.bound, () => slots.value.length, bindingsLoading, connectionsLoading],
  () => { void handleAutoBind(); },
  { immediate: true },
);
</script>

<template>
  <div class="tab-pane fade show active">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <div>
        <h2 class="mb-1">Required Connections</h2>
        <p class="text-muted mb-0">
          Workflows in this project reference these connection slots. Bind each slot to one of
          your organisation's connections of the matching type.
        </p>
      </div>
    </div>

    <div v-if="bindingsLoading" class="text-center text-muted py-4">
      <div class="spinner-border" role="status"></div>
    </div>

    <div v-else-if="bindingsError" class="alert alert-danger">
      <i class="bi bi-exclamation-triangle me-2"></i>
      Failed to load connection bindings. {{ bindingsError.userMessage }}
    </div>

    <div v-else-if="slots.length === 0" class="alert alert-info">
      <i class="bi bi-info-circle me-2"></i>
      No connection slots are declared by this project's published workflows yet. Add a connection to
      a workflow's service in the builder, then publish the workflow — its slots appear here to bind.
    </div>

    <div v-else class="card">
      <div class="card-body">
        <div
          v-for="slot in slots"
          :key="slotKey(slot)"
          class="border rounded p-3 mb-3 bg-body-secondary"
        >
          <div class="d-flex flex-column flex-md-row justify-content-between align-items-stretch align-items-md-start gap-3">
            <div class="flex-grow-1 min-w-0">
              <div class="d-flex align-items-center gap-2 mb-1">
                <span class="fw-semibold">{{ slot.slotName }}</span>
                <span
                  v-if="!slot.boundConnectionId"
                  class="badge bg-warning text-dark"
                  title="No org connection bound — runtime resolution will return empty values."
                >
                  Needs binding
                </span>
              </div>
            </div>

            <div class="d-flex flex-column gap-2 slot-controls">
              <select
                class="form-select form-select-sm"
                :value="currentSelection(slot)"
                :disabled="isPending(slot)"
                @change="setDraft(slot, ($event.target as HTMLSelectElement).value)"
              >
                <option value="">— Pick a connection —</option>
                <option
                  v-for="conn in connectionsForSlot(slot)"
                  :key="conn.id"
                  :value="conn.id"
                >
                  {{ conn.name }}
                </option>
              </select>

              <small v-if="connectionsForSlot(slot).length === 0" class="text-muted">
                No {{ slot.connectionTemplateName ?? 'matching' }} connections in your organisation yet —
                <router-link
                  :to="{
                    name: 'connection-create',
                    params: { templateId: slot.connectionTemplateId },
                    query: { returnProjectId: projectIdRef, slotName: slot.slotName },
                  }"
                >
                  create one
                </router-link>
                to bind this slot.
              </small>

              <div class="d-flex gap-2 justify-content-end">
                <button
                  v-if="slot.boundConnectionId"
                  class="bb-btn-secondary-cta btn-sm"
                  :disabled="isPending(slot)"
                  @click="clearBinding(slot)"
                >
                  <span
                    v-if="isPending(slot) && unbindConnectionPending"
                    class="spinner-border spinner-border-sm me-1"
                    role="status"
                  ></span>
                  <i v-else class="bi bi-x-circle me-1"></i>
                  Clear
                </button>
                <button
                  class="bb-btn-primary-cta btn-sm"
                  :disabled="isPending(slot) || !hasChange(slot) || !currentSelection(slot)"
                  @click="saveBinding(slot)"
                >
                  <span
                    v-if="isPending(slot) && bindConnectionPending"
                    class="spinner-border spinner-border-sm me-1"
                    role="status"
                  ></span>
                  <i v-else class="bi bi-check-circle me-1"></i>
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>

        <div v-if="bindConnectionError" class="alert alert-danger mt-3">
          <i class="bi bi-exclamation-triangle me-2"></i>
          {{ bindConnectionError.userMessage }}
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
// The controls column holds a comfortable width on desktop, but on mobile the row stacks
// (flex-column) so it must go full-width — a fixed min-width there overflows the viewport.
.slot-controls {
  @media (min-width: 768px) {
    min-width: 280px;
  }
}
</style>
